'use client';

import { useRef, useState, type JSX } from 'react';

import { cn } from '~/utils';
import { Chip } from '~/components/chip';
import { Show } from '~/components/show';
import { Text } from '~/components/text';
import { Button } from '~/components/button';
import { CaretDownIcon, CheckIcon } from '~/components/icons';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/popover';
import {
  Command,
  CommandItem,
  CommandList,
  CommandGroup,
} from '~/components/command';

interface IOptionProps {
  label: string;
  value: string;
}

interface ISelectChipProps {
  value?: string[];
  placeholder?: string;
  options?: IOptionProps[];
  onChange?: (value: string[]) => void;
}

export function SelectChip({
  onChange,
  value = [],
  placeholder,
  options = [],
  ...rest
}: ISelectChipProps): JSX.Element {
  const containerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(value);

  const handleSelect = (value: string) => {
    let newValue = selected;

    if (selected.includes(value)) {
      newValue = selected.filter((v) => v !== value);
      setSelected(newValue);
    } else {
      newValue = [...selected, value];
      setSelected(newValue);
    }

    onChange?.(newValue);
  };

  const handleRemove = (value?: string) => {
    const newValue = selected.filter((v) => v !== value);
    setSelected(newValue);
    onChange?.(newValue);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          {...rest}
          role='combobox'
          variant='outline'
          ref={containerRef}
          aria-expanded={open}
          className={cn(
            'w-full justify-between border-[#ececec] px-4 py-2 min-h-[46px] h-[unset]',
            'hover:border-[#cdcdcd] hover:bg-transparent active:bg-transparent',
            open && 'border-blueBonnet-500 hover:border-blueBonnet-500',
          )}
        >
          <div className='flex flex-wrap gap-2 items-center min-h-[30px]'>
            <Show when={!selected.length}>
              <Text className='text-[#bfbfbf]'>{placeholder}</Text>
            </Show>
            {!!options.length &&
              selected.map((v) => {
                const { label = '', value = '' } =
                  options.find((o) => o.value === v) || {};

                return (
                  <Chip
                    key={v}
                    label={label}
                    value={value}
                    onRemove={handleRemove}
                  />
                );
              })}
          </div>
          <CaretDownIcon className='size-4' />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align='start'
        side='bottom'
        sideOffset={8}
        className='p-0 w-full'
        style={{
          width: containerRef.current
            ? `${containerRef.current.offsetWidth}px`
            : '100%',
        }}
      >
        <Command className='w-full'>
          <CommandList>
            <CommandGroup className='p-0'>
              {options.map((option) => {
                return (
                  <SelectChipItem
                    key={option.value}
                    onSelect={handleSelect}
                    selected={selected.includes(option.value)}
                    {...option}
                  />
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

interface ISelectChipItemProps {
  label: string;
  value: string;
  selected: boolean;
  onSelect: (value: string) => void;
}

function SelectChipItem({
  label,
  value,
  selected,
  onSelect,
}: ISelectChipItemProps): JSX.Element {
  return (
    <CommandItem
      value={value}
      onSelect={onSelect}
      className='flex items-center py-3 px-4 gap-2'
    >
      <span
        className={cn(
          'flex size-5 items-center justify-center rounded-[4px] bg-blueBonnet-50',
          '[--svg-color:var(--color-white)]',
          selected && 'bg-blueBonnet-500',
        )}
      >
        <Show when={selected}>
          <CheckIcon variant='stroke' className='size-3' />
        </Show>
      </span>
      <Text className='text-[16px]'>{label}</Text>
    </CommandItem>
  );
}
